import type { AssistantRunRequest, AssistantStreamEvent } from '@fieldops/shared'
import { apiOrigin, currentBearerToken } from './trpc.js'

/**
 * One exchange with the job assistant, read as it arrives.
 *
 * `POST /assistant/run` answers NDJSON — one `AssistantStreamEvent` per line —
 * rather than going through tRPC, because the reply comes a few words at a
 * time. Plain JSON, so dates arrive as strings. A refusal before the stream
 * starts (not signed in, not allowed, not connected) is thrown with the
 * server's own words. Decision 94.
 */
export async function runAssistant(
  request: AssistantRunRequest,
  onEvent: (event: AssistantStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = currentBearerToken()
  const response = await fetch(`${apiOrigin}/assistant/run`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
    signal,
  })

  if (!response.ok || !response.body) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(payload?.error ?? `The assistant did not answer (${response.status}).`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const line = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    try {
      onEvent(JSON.parse(trimmed) as AssistantStreamEvent)
    } catch {
      // A torn line is dropped; `done` carries the saved conversation anyway.
    }
  }

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let newline = buffer.indexOf('\n')
    while (newline !== -1) {
      line(buffer.slice(0, newline))
      buffer = buffer.slice(newline + 1)
      newline = buffer.indexOf('\n')
    }
  }
  line(buffer + decoder.decode())
}
